// ---------------------------------------------------------------
// Dispatch workspace data rules. Pure functions only (unit-tested in
// model.test.js) — every screen reads trips through these so the rules live
// in ONE place:
//   • missing data stays "Not reported" — never 0, never "healthy", never solo
//   • zero stays zero ("0m")
//   • stop order comes from the uploaded trip sheet; never sorted by ETA
//   • a geofence visit is NOT a delivery; TruckMate delivery status is shown
//     separately and never changed here
// ---------------------------------------------------------------

export const NOT_REPORTED = 'Not reported';
const MIN = 60000;
const HOS_STALE_MIN = 15;
const GPS_STALE_MIN = 30;

// ---- formatting ---------------------------------------------------------------
export function fmtMins(m) {
  if (m == null || Number.isNaN(Number(m))) return NOT_REPORTED;
  const v = Math.max(0, Math.round(Number(m)));
  if (v < 60) return `${v}m`;
  const h = Math.floor(v / 60); const r = v % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}
export function ago(iso, now = Date.now()) {
  const t = typeof iso === 'number' ? iso : Date.parse(iso || '');
  if (!t || Number.isNaN(t)) return null;
  const m = Math.round((now - t) / MIN);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${m % 60}m ago` : `${Math.floor(h / 24)}d ago`;
}
export function minutesSince(iso, now = Date.now()) {
  const t = typeof iso === 'number' ? iso : Date.parse(iso || '');
  return !t || Number.isNaN(t) ? null : (now - t) / MIN;
}
export const shortDate = (ms) => (ms == null ? null : new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }));
const has = (v) => v != null && v !== '';
const cityOf = (label) => String(label || '').split(',')[0].trim().toUpperCase();
const stateOf = (label) => { const p = String(label || '').split(',').map((x) => x.trim()); return (p[1] || '').toUpperCase(); };
// SAINT LOUIS = ST LOUIS, FORT/FT, MOUNT/MT — same rule as the server's trip-sheet reader
const normCity = (c) => String(c || '').trim().toUpperCase().replace(/[^A-Z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim()
  .replace(/^SAINTE /, 'STE ').replace(/^SAINT /, 'ST ').replace(/^FORT /, 'FT ').replace(/^MOUNT /, 'MT ');
const cityKey = (city, state) => `${normCity(city)}|${String(state || '').trim().toUpperCase()}`;
// a sheet stop the server paired with a differently-named TruckMate town (tmPlace)
const sheetKey = (s) => (s.tmPlace ? cityKey(cityOf(s.tmPlace), stateOf(s.tmPlace)) : cityKey(s.city, s.state));

// ---- identity -----------------------------------------------------------------
// Full freight-bill identifiers exactly as TruckMate sends them (B…, H…, L…,
// leading zeros kept), unique, in source order. Never the trip number.
export function billIds(trip) {
  const out = [];
  for (const b of (trip && trip.bills) || []) {
    const id = b && b.billNumber != null ? String(b.billNumber) : '';
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}
export function identity(trip) {
  const bills = billIds(trip);
  return {
    truck: trip.powerUnit ? String(trip.powerUnit) : null,
    trailer: trip.trailer ? String(trip.trailer) : null,   // THIS trip's trailer, not a permanent pairing
    trip: String(trip.tripNumber || ''),
    bills,
    leadBill: bills[0] || null,
    moreBills: Math.max(0, bills.length - 1),
  };
}

// Active trips ordered by bill number (B180212 before B180251 before M5035871),
// so trucks on related bills sit together. The lead bill is the trip's first
// bill — or, with a bill-type filter on, its first bill of that type. Trips with
// no bill go last.
const billParts = (id) => {
  const m = String(id || '').toUpperCase().match(/^([A-Z]*)(\d*)(.*)$/) || [];
  return [m[1] || '', m[2] ? Number(m[2]) : Infinity, m[3] || ''];
};
export function leadBillFor(trip, type = 'all') {
  const ids = billIds(trip);
  if (type && type !== 'all') return ids.find((b) => b.toUpperCase().startsWith(type)) || ids[0] || null;
  return ids[0] || null;
}
export function compareByBill(a, b, type = 'all') {
  const x = leadBillFor(a, type); const y = leadBillFor(b, type);
  if (!x || !y) return x ? -1 : (y ? 1 : 0);
  const [xp, xn, xr] = billParts(x); const [yp, yn, yr] = billParts(y);
  return xp.localeCompare(yp) || (xn - yn) || xr.localeCompare(yr) || String(a.tripNumber).localeCompare(String(b.tripNumber));
}

const last10 = (p) => String(p || '').replace(/\D+/g, '').slice(-10);
// Who dispatch texts/calls about this load: the OC driver, else company driver 1.
export function contactFor(trip) {
  if (trip.oc) {
    const p = trip.oc.driverPhone;
    const rec = p ? (trip.driverConsent || {})[last10(p)] : null;
    return p ? { phone: p, name: trip.oc.driverName || 'driver', consent: rec && rec.optedOut ? null : (trip.oc.smsConsent || rec || null), optedOut: rec && rec.optedOut ? rec.optedOut : null, kind: 'oc', which: 1 } : null;
  }
  const info = trip.live && trip.live.driver1Info;
  if (!info || !info.phone) return null;
  const rec = (trip.driverConsent || {})[last10(info.phone)] || null;
  return { phone: info.phone, name: info.name || trip.live.driver1 || 'driver', consent: rec && rec.optedOut ? null : rec, optedOut: rec && rec.optedOut ? rec.optedOut : null, kind: 'company', which: 1 };
}

// When the trip was created (TruckMate), added to AI Dispatcher, and dispatched.
export function timesModel(trip) {
  const t = trip.times;
  if (!t) return null;
  const at = (iso) => (iso ? shortDate(Date.parse(iso)) : null);
  return {
    created: t.createdAt ? `${at(t.createdAt)}${t.createdBy ? ` · ${t.createdBy}` : ''}` : null,
    createdNote: t.createdFrom === 'first freight bill' ? 'first freight bill entered' : null,
    added: t.addedAt ? (t.addedBefore ? `before ${at(t.addedAt)}` : at(t.addedAt)) : null,
    dispatched: t.dispatchedAt ? at(t.dispatchedAt) : (t.dispatchedBefore ? `before ${at(t.dispatchedBefore)}` : (t.noTruck ? 'No truck assigned yet' : 'Not dispatched yet')),
    dispatchedKnown: !!t.dispatchedAt,
  };
}

// ---- location ----------------------------------------------------------------
export function locationModel(trip, now = Date.now()) {
  const l = trip.live || {};
  const gpsAge = minutesSince(l.gpsAt, now);
  const src = l.source === 'driver app' ? 'Driver phone (TagAlong link)' : (l.source && String(l.source).includes('traccar') ? 'FMC00A' : (l.source ? 'Samsara' : null));
  return {
    // the driver's phone shows as City, ST (looked up by the server) — never raw coordinates
    gps: has(l.location) || l.lat != null ? (l.location || (l.source === 'driver app' ? 'Locating city…' : `${Number(l.lat).toFixed(3)}, ${Number(l.lng).toFixed(3)}`)) : null,
    gpsAge: gpsAge == null ? null : ago(l.gpsAt, now),
    gpsStale: gpsAge == null ? true : gpsAge > GPS_STALE_MIN,
    source: src,
    truckmateZone: trip.current || null,   // TruckMate's last reported zone — NOT the GPS position
    speedMph: l.speedMph != null ? Number(l.speedMph) : null,
  };
}

// ---- crew & hours ---------------------------------------------------------------
export const DUTY_LABEL = {
  driving: 'Driving', onDuty: 'On duty', offDuty: 'Off duty', sleeperBerth: 'Sleeper berth', sleeperBed: 'Sleeper berth',
  yardMove: 'Yard move', personalConveyance: 'Personal conveyance', waitingAtWellSite: 'Waiting',
};
export function dutyLabel(status) {
  if (!status) return 'Unknown';
  return DUTY_LABEL[status] || String(status).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

// Per-driver rows with INDEPENDENT clocks. "At the wheel" only when one
// driver's fresh ELD record says Driving in THIS truck.
// "2026-10-31 01:00" → "Oct 31 · 1:00 AM": how it reads, exactly the clock time written, no time zone applied. Pure.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function friendlyAppt(x) {
  const t = String(x || '').trim();
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/);
  const us = t.match(/^(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?(?: (\d{1,2}):(\d{2}))?$/);
  const [mo, day, hh, mm] = iso ? [iso[2], iso[3], iso[4], iso[5]] : us ? [us[1], us[2], us[3], us[4]] : [];
  if (mo == null || !MONTHS[Number(mo) - 1]) return x;
  const date = `${MONTHS[Number(mo) - 1]} ${Number(day)}`;
  if (hh == null) return date;
  const h = Number(hh);
  return `${date} · ${h % 12 || 12}:${mm} ${h < 12 ? 'AM' : 'PM'}`;
}
// "7869533421" or "+17869533421" → "(786) 953-3421"; anything else stays as written. Pure.
export const phoneLabel = (p) => { const d = String(p || '').replace(/\D+/g, ''); const n = d.length === 11 && d[0] === '1' ? d.slice(1) : d; return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : (p || ''); };
// "Stop 6 · 2026-10-03 01:00 (handwritten)" → its stop, the time and whether it was handwritten. Pure.
export function apptParts(a) {
  const m = String(a || '').match(/^(?:Stop (\S+) · )?(.*?)( \(handwritten\))?$/);
  return { stop: m[1] || null, when: friendlyAppt(m[2]), handwritten: !!m[3] };
}
// "Stop 2 · Sample Hub — Swedesboro, NJ" → number, name, city. Anything else stays one label. Pure.
export function nextParts(label) {
  const m = String(label || '').match(/^Stop (\S+) · (.*)$/);
  const rest = m ? m[2] : String(label || '');
  const i = m ? rest.lastIndexOf(' — ') : -1;
  return { stop: m ? m[1] : null, name: i > 0 ? rest.slice(0, i) : rest, city: i > 0 ? rest.slice(i + 3) : null };
}
// "MON-FRI => 1:00 AM - 2:00 PM; SAT-SUN => 1:00 AM - 11:00 AM" → day / hours rows, words as written; null if it reads any other way. Pure.
export function hoursRows(x) {
  const rows = String(x || '').split(/;\s*/).filter(Boolean).map((r) => r.match(/^(.*?)\s*=>\s*(.*)$/));
  return rows.length && rows.every(Boolean) ? rows.map((r) => [r[1], r[2]]) : null;
}

// The phone printed for this driver on the trip sheet: matched by driver ID, then by the same name
// words ("ROJAS, CARLOS" = "Carlos Rojas"), never by position. Pure.
const nameWords = (x) => String(x || '').toUpperCase().replace(/[^A-Z ]/g, ' ').split(/\s+/).filter(Boolean).sort().join(' ');
export function sheetPhoneFor(trip, code, name) {
  const list = (trip.manifest && trip.manifest.drivers) || [];
  const byId = code ? list.find((d) => d && d.phone && String(d.id || '').trim() === String(code).trim()) : null;
  const byName = name ? list.find((d) => d && d.phone && nameWords(d.name) && nameWords(d.name) === nameWords(name)) : null;
  return (byId || byName || {}).phone || null;
}

export function crewModel(trip, now = Date.now()) {
  const l = trip.live || {};
  const unit = trip.powerUnit ? String(trip.powerUnit) : null;
  const slots = [
    { code: trip.driver || null, name: l.driver1 || null, hos: l.hos || null, info: l.driver1Info || null },
    { code: trip.driver2 || null, name: l.driver2 || null, hos: l.hos2 || null, info: l.driver2Info || null },
  ].filter((d) => d.code || d.name);
  const drivers = slots.map((d, i) => {
    const h = d.hos || null;
    const age = h && h.at ? minutesSince(h.at, now) : null;
    const stale = !h || age == null || age > HOS_STALE_MIN;
    const elsewhere = !!(h && h.vehicle && unit && String(h.vehicle) !== unit);
    const samsaraPhone = d.info && d.info.phone ? d.info.phone : null;
    const sheetPhone = sheetPhoneFor(trip, d.code, d.name);
    return {
      role: i === 0 ? 'Driver 1' : 'Driver 2',
      code: d.code,
      name: d.name || (d.code ? `Driver ${d.code}` : 'Unknown driver'),
      matched: !!d.name,                         // resolved in Samsara
      status: h ? dutyLabel(h.status) : 'Unknown',
      statusKey: h ? h.status || null : null,
      shiftLeft: h ? fmtMins(h.shiftLeftMin) : NOT_REPORTED,
      driveLeft: h ? fmtMins(h.driveLeftMin) : NOT_REPORTED,
      cycleLeft: h && h.cycleLeftMin != null ? fmtMins(h.cycleLeftMin) : null,
      breakIn: h && h.breakInMin != null ? fmtMins(h.breakInMin) : null,
      driveLeftMin: h && h.driveLeftMin != null ? h.driveLeftMin : null,
      vehicle: h && h.vehicle ? String(h.vehicle) : null,
      elsewhere,
      stale,
      sourceAge: h && h.at ? ago(h.at, now) : null,
      info: d.info,                                // Samsara driver profile (phone, licence, ELD…)
      phone: samsaraPhone || sheetPhone || null,      // Samsara first, the trip sheet as the backup
      phoneSource: samsaraPhone ? 'Samsara' : sheetPhone ? 'Trip sheet' : null,
      // the sheet lists a different number than Samsara: shown, never swapped in
      sheetPhoneDiffers: samsaraPhone && sheetPhone && last10(samsaraPhone) !== last10(sheetPhone) ? sheetPhone : null,
      consent: d.info && d.info.phone && !((trip.driverConsent || {})[last10(d.info.phone)] || {}).optedOut ? (trip.driverConsent || {})[last10(d.info.phone)] || null : null,
      optedOut: d.info && d.info.phone ? ((trip.driverConsent || {})[last10(d.info.phone)] || {}).optedOut || null : null,
      which: i + 1,
    };
  });
  const allMatched = drivers.length > 0 && drivers.every((d) => d.matched);
  let mode = 'Unverified';
  if (allMatched && trip.driver2) mode = 'Team';
  else if (allMatched && !trip.driver2) mode = 'Solo';

  const fresh = drivers.filter((d) => !d.stale);
  const drivingHere = fresh.filter((d) => d.statusKey === 'driving' && !d.elsewhere);
  const drivingElsewhere = fresh.filter((d) => d.statusKey === 'driving' && d.elsewhere);
  let atWheel = { verified: false, name: null, note: 'Not verified' };
  const conflicts = [];
  if (drivingHere.length === 1) atWheel = { verified: true, name: drivingHere[0].name, note: 'Driving status on this truck (ELD)' };
  else if (drivingHere.length > 1) { atWheel = { verified: false, name: null, note: 'Conflict' }; conflicts.push('Both drivers report Driving on this truck.'); }
  else if (drivers.length && fresh.length === drivers.length) atWheel = { verified: false, name: null, note: 'No driver in Driving status' };
  drivingElsewhere.forEach((d) => conflicts.push(`${d.name} is Driving on truck ${d.vehicle}, not ${unit}.`));
  drivers.filter((d) => d.elsewhere && d.statusKey !== 'driving').forEach((d) => conflicts.push(`${d.name}'s ELD is logged into truck ${d.vehicle}.`));
  if (l.samsaraDriver && drivers.length && !drivers.some((d) => d.name && d.name.toLowerCase() === String(l.samsaraDriver).toLowerCase())) {
    conflicts.push(`Samsara assigns ${l.samsaraDriver} to this truck; the trip lists ${drivers.map((d) => d.name).join(' / ')}.`);
  }
  const anyHos = drivers.some((d) => d.statusKey || d.driveLeftMin != null);
  return {
    mode,
    drivers,
    atWheel,
    conflicts,
    source: anyHos ? `Assignment: TruckMate · Duty clocks: Samsara ELD${drivers[0] && drivers[0].sourceAge ? ` · read ${drivers[0].sourceAge}` : ''}` : 'Duty status and clocks not reported by the ELD for this trip.',
  };
}

// ---- truck health -----------------------------------------------------------------
const v = (x, unit = '') => (x == null || x === '' ? null : `${x}${unit}`);
export function healthModel(trip, now = Date.now()) {
  const l = trip.live || null;
  const faults = (l && Array.isArray(l.dtcCodes)) ? l.dtcCodes : [];
  const engineSeen = !!(l && [l.power, l.fuelPct, l.coolantF, l.rpm, l.defPct, l.engineLoadPct].some((x) => x != null));
  let status = { key: 'unknown', label: 'Unknown · No telemetry' };
  if (faults.length) status = { key: 'warning', label: `Needs attention · ${faults.length} active fault${faults.length === 1 ? '' : 's'}` };
  else if (engineSeen) status = { key: 'ok', label: 'No active faults reported' };
  else if (l && l.gpsAt) status = { key: 'unknown', label: 'Unknown · GPS only, no engine data' };
  const src = l && l.source && String(l.source).includes('traccar') ? 'FMC00A' : (l ? 'Samsara' : null);
  const groups = [
    { title: 'Location & movement', rows: [
      ['Current location', l && l.location],
      ['GPS update', l && l.gpsAt ? ago(l.gpsAt, now) : null],
      ['Speed', l && l.speedMph != null ? `${Math.round(l.speedMph)} mph` : null],
      ['Ignition', l && l.ignition != null ? (l.ignition ? 'On' : 'Off') : null],
      ['Engine state', l && l.engine],
      ['Odometer', l && l.odometerMi != null ? `${Number(l.odometerMi).toLocaleString()} mi` : null],
      ['Engine hours', l && l.engineHours != null ? `${Number(l.engineHours).toLocaleString()} h` : null],
    ] },
    { title: 'Engine & fluids', rows: [
      ['Coolant temperature', l && v(l.coolantF, '°F')],
      ['Oil pressure', l && v(l.oilPressurePsi, ' psi')],
      ['Battery voltage', l && l.power != null ? `${Number(l.power).toFixed(1)} V` : null],
      ['Fuel level', l && v(l.fuelPct, '%')],
      ['DEF level', l && v(l.defPct, '%')],
      ['RPM', l && l.rpm != null ? String(Math.round(l.rpm)) : null],
      ['Engine load', l && v(l.engineLoadPct, '%')],
    ] },
    { title: 'Faults & emissions', rows: [
      ['Active faults', l ? (faults.length ? `${faults.length}` : (engineSeen ? '0 reported' : null)) : null],
      ...faults.map((f) => [f.code, f.meaning || 'No description']),
      ['Check-engine lamp', null],
      ['DPF soot / regeneration', null],
    ] },
    { title: 'Tires & brakes', rows: [['Tire pressure / temperature', null], ['Brake system / ABS', null], ['Air pressure', null]], sensor: true },
    { title: 'Trailer & reefer', rows: [
      ['Trip trailer', trip.trailer || null],
      ['Trailer connection', null],
      ['Reefer temperature', l && l.tempF != null ? `${l.tempF}°F${l.tempStale ? ' (stale reading)' : ''}` : null],
      ['Setpoint', l && v(l.setpointF, '°F')],
      ['Supply air', l && v(l.supplyF, '°F')],
      ['Reefer state', l && l.reeferState],
      ['Reefer fuel', l && v(l.reeferFuel, '%')],
      ['Door / humidity', null],
    ] },
    { title: 'Maintenance & utilization', rows: [['Next / last service', null], ['Idle time', null], ['Fuel economy', null], ['Harsh events', null], ['Last inspection', null]], sensor: true },
  ].map((g) => ({ ...g, rows: g.rows.map(([label, val]) => ({ label, value: val == null || val === '' ? (g.sensor ? 'Sensor unavailable' : NOT_REPORTED) : String(val), missing: val == null || val === '' })) }));
  return {
    status,
    summary: [
      { label: 'Fuel', value: l && l.fuelPct != null ? `${l.fuelPct}%` : NOT_REPORTED },
      { label: 'DEF', value: l && l.defPct != null ? `${l.defPct}%` : NOT_REPORTED },
      { label: 'Battery', value: l && l.power != null ? `${Number(l.power).toFixed(1)} V` : NOT_REPORTED },
      { label: 'Active faults', value: faults.length ? String(faults.length) : (engineSeen ? '0 reported' : NOT_REPORTED) },
    ],
    faults,
    groups,
    source: l ? `${src}${l.gpsAt ? ` · GPS ${ago(l.gpsAt, now)}` : ''}${faults.length ? '' : (engineSeen ? ' · "No faults" is the provider’s report, not a safety check' : '')}` : 'No telemetry matched to this truck — health cannot be assessed.',
  };
}

// ---- outbound stops ---------------------------------------------------------------
export const VISIT_LABEL = {
  completed: 'Visit complete', at_stop: 'At stop', arriving: 'Arriving', departing: 'At stop · leaving',
  needs_review: 'Needs review', upcoming: 'Upcoming',
};
// Ordered stops for a trip. Source of order, in priority:
//   1. uploaded trip sheet (printed stop numbers + sub-stops, as given)
//   2. rate confirmation (pickups, then deliveries, as listed)
//   3. TruckMate bills in SOURCE order, flagged "Sequence unverified"
export function stopsModel(trip) {
  const bills = trip.bills || [];
  const sheet = trip.manifest;
  const visits = trip.visits || {};
  const billsByCity = new Map();
  bills.forEach((b) => {
    const k = cityKey(cityOf(b.to), stateOf(b.to));
    if (!billsByCity.has(k)) billsByCity.set(k, []);
    billsByCity.get(k).push(b);
  });
  const tmDelivered = (k) => { const bs = billsByCity.get(k) || []; return bs.length > 0 && bs.every((b) => b.delivered); };

  let stops = []; let source = 'none'; let verified = false;
  if (sheet && Array.isArray(sheet.stops) && sheet.stops.length) {
    source = 'Trip sheet'; verified = true;
    const perCity = {};
    sheet.stops.forEach((s) => { const k = sheetKey(s); perCity[k] = (perCity[k] || 0) + 1; });
    stops = sheet.stops.map((s, i) => {
      const k = sheetKey(s);
      const visit = s.key ? visits[s.key] || null : null;
      return {
        key: s.key || `sheet-${i}`,
        number: s.stopNumber,
        subStop: !!s.subStop,
        action: /LOAD|PICKUP/i.test(s.action || '') ? 'Pickup' : (/DELIVER/i.test(s.action || '') ? 'Delivery' : (s.action || 'Stop')),
        customer: s.customer || null,
        address: s.address || null,
        place: [s.city, s.state, s.zip].filter(Boolean).join(', ') || null,
        appointment: s.apptDate || s.apptTime ? `${s.apptDate || ''} ${s.apptTime || ''}`.trim() : null,
        appointmentSource: s.apptSource || null,
        receivingHours: s.receivingHours || null,
        quantity: s.piecesText || (s.pieces != null ? `${s.pieces} pcs` : null),
        references: s.references || [],
        instructions: s.instructions || [],
        handwritten: s.handwritten || [],
        callAhead: s.callAhead || [],
        bills: /DELIVER/i.test(s.action || '') ? (billsByCity.get(k) || []).map((b) => b.billNumber) : [],
        billsShared: perCity[k] > 1,
        tmDelivered: /DELIVER/i.test(s.action || '') && tmDelivered(k),
        visit,
        tracked: !!visit,
      };
    });
  } else if (trip.rateCon && ((trip.rateCon.pickups || []).length || (trip.rateCon.deliveries || []).length)) {
    source = 'Rate confirmation'; verified = true;
    const rc = trip.rateCon;
    stops = [...(rc.pickups || []).map((s) => ({ ...s, _a: 'Pickup' })), ...(rc.deliveries || []).map((s) => ({ ...s, _a: 'Delivery' }))].map((s, i) => ({
      key: `rc-${i}`, number: i + 1, subStop: false, action: s._a, customer: s.name || null, address: s.address || null,
      place: [s.city, s.state, s.zip].filter(Boolean).join(', ') || null,
      appointment: [s.date, s.time || s.appointment].filter(Boolean).join(' ') || null, appointmentSource: 'rate con',
      receivingHours: null, quantity: null, references: s.refs ? [String(s.refs)] : [], instructions: [], handwritten: [], callAhead: [],
      bills: s._a === 'Delivery' ? (billsByCity.get(cityKey(s.city, s.state)) || []).map((b) => b.billNumber) : [],
      billsShared: false, tmDelivered: s._a === 'Delivery' && tmDelivered(cityKey(s.city, s.state)), visit: null, tracked: false,
    }));
  } else if (bills.length) {
    source = 'TruckMate bills'; verified = false;
    const groups = new Map();
    bills.forEach((b) => {                                    // SOURCE order — never ETA order
      const k = `${b.billTo || '?'}@@${b.to || '?'}`;
      if (!groups.has(k)) groups.set(k, { customer: b.billTo || null, place: b.to || null, bills: [], pieces: 0, delivered: true, deliverBy: b.deliverBy, deliverByEnd: b.deliverByEnd });
      const g = groups.get(k); g.bills.push(b.billNumber); g.pieces += b.pieces || 0; if (!b.delivered) g.delivered = false;
    });
    stops = [...groups.values()].map((g, i) => ({
      key: `tm-${i}`, number: null, subStop: false, action: 'Delivery', customer: g.customer, address: null, place: g.place,
      appointment: null, appointmentSource: null, window: g.deliverBy ? `${g.deliverBy.slice(0, 16).replace('T', ' ')} – ${(g.deliverByEnd || '').slice(0, 16).replace('T', ' ')}` : null,
      receivingHours: null, quantity: g.pieces ? `${g.pieces} pcs` : null, references: [], instructions: [], handwritten: [], callAhead: [],
      bills: g.bills, billsShared: false, tmDelivered: g.delivered, visit: null, tracked: false,
    }));
  }
  const confirms = trip.stopConfirm || {};
  stops.forEach((s) => {
    s.driverConfirm = confirms[s.key] || null;     // driver replied YES by text
    const st = s.visit ? s.visit.state : null;
    s.status = st === 'completed' ? 'completed' : (st === 'at_stop' || st === 'departing' || st === 'arriving') ? 'at-stop' : st === 'needs_review' ? 'review' : 'upcoming';
    s.statusLabel = s.visit ? VISIT_LABEL[st] || st : (s.tracked ? 'Upcoming' : 'Upcoming');
    if (s.driverConfirm && s.status !== 'completed') { s.status = 'completed'; s.statusLabel = 'Driver confirmed delivered'; }
    // TruckMate says delivered → say so (not "Upcoming"), with when
    if (s.tmDelivered) {
      const at = bills.filter((b) => (s.bills || []).includes(b.billNumber) && b.deliveredAt).map((b) => String(b.deliveredAt)).sort().pop() || null;
      s.deliveredAt = at;
      if (s.statusLabel === 'Upcoming') s.statusLabel = 'Delivered';
    }
  });
  const current = stops.find((s) => s.status === 'at-stop') || null;
  const next = current ? null : stops.find((s) => s.status !== 'completed' && !s.tmDelivered && s.action !== 'Pickup') || null;
  const tracked = stops.filter((s) => s.tracked).length;
  const completed = stops.filter((s) => s.status === 'completed').length;
  const tmDeliveredCount = stops.filter((s) => s.tmDelivered).length;
  return { stops, source, verified, current, next, tracked, completed, tmDeliveredCount, total: stops.length, changes: sheet && sheet.changes ? sheet.changes : null };
}

// ---- documents ----------------------------------------------------------------
// Originals grouped per kind; newest upload first. Old uploads without stored
// bytes are reported honestly (no fake "View PDF").
export function documentsModel(trip) {
  const docs = trip.docs || [];
  const byKind = (kind) => {
    const list = docs.filter((d) => d.kind === kind).sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt)) || (a.page || 0) - (b.page || 0));
    const groups = [];
    list.forEach((d) => {
      const gk = kind === 'ratecon' ? `v${d.version || '?'}` : (d.batchId || d.uploadedAt);
      let g = groups.find((x) => x.key === gk);
      if (!g) { g = { key: gk, version: d.version || null, uploadedAt: d.uploadedAt, uploadedBy: byLabel(d.uploadedBy), files: [] }; groups.push(g); }
      g.files.push(d);
    });
    return groups;
  };
  const rc = trip.rateCon || null;
  const sheet = trip.manifest || null;
  const ratecon = byKind('ratecon');
  const tripsheet = byKind('tripsheet');
  const driverdoc = byKind('driverdoc');   // POD / BOL photos the driver sent from the tracking link
  const artisdoc = byKind('artisdoc');     // POD / BOL / invoice / rate con from ARTIS (Synergize), by bill
  const manifest = byKind('manifest');     // ARTIS Trip Manifest PDFs, one per customer
  const stateOfDoc = (extracted, groups) => {
    if (!extracted && !groups.length) return 'No document';
    if (extracted && extracted.raw) return 'Needs review';
    if (extracted) return 'Extracted';
    return 'Stored · not extracted';
  };
  return {
    ratecon: { groups: ratecon, latest: ratecon[0] || null, extracted: rc, state: stateOfDoc(rc, ratecon), originalMissing: !!rc && !ratecon.length },
    tripsheet: { groups: tripsheet, latest: tripsheet[0] || null, extracted: sheet, state: stateOfDoc(sheet, tripsheet), originalMissing: !!sheet && !tripsheet.length },
    driverdoc: { groups: driverdoc, latest: driverdoc[0] || null },
    artisdoc: { groups: artisdoc, latest: artisdoc[0] || null },
    manifest: { groups: manifest, latest: manifest[0] || null },
  };
}
export const viewLabel = (file) => (file && file.isPdf ? 'View PDF' : 'View original');

// ---- rate con ----------------------------------------------------------------
export function rateConModel(rc) {
  if (!rc) return null;
  const pickups = Array.isArray(rc.pickups) ? rc.pickups : [];
  const deliveries = Array.isArray(rc.deliveries) ? rc.deliveries : [];
  const place = (s) => [s.city, s.state].filter(Boolean).join(', ');
  const temp = (() => {
    const t = rc.tempSetting; if (!has(t)) return null;
    const m = String(t).match(/(-?\d+(?:\.\d+)?)\s*(?:°|deg|F)?\s*(?:-|to|–)\s*(-?\d+(?:\.\d+)?)/i);
    if (m && m[1] === m[2]) return String(t).replace(m[0], `${m[1]}°`);   // equal min/max shows once
    return String(t);
  })();
  return {
    load: rc.loadNumber || null,
    route: { from: pickups[0] ? place(pickups[0]) : null, to: deliveries.length ? place(deliveries[deliveries.length - 1]) : null },
    rate: rc.rateText || (rc.rate != null ? `$${Number(rc.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null),
    strip: [['Equipment', rc.equipment], ['Temperature', temp], ['Weight', rc.weight], ['Commodity', rc.commodity]].map(([k, val]) => ({ label: k, value: has(val) ? String(val) : NOT_REPORTED })),
    stops: [...pickups.map((s) => ({ ...s, kind: 'Pickup' })), ...deliveries.map((s) => ({ ...s, kind: 'Delivery' }))],
    instructions: [...new Set((rc.specialInstructions || []).map((x) => String(x).trim()).filter(Boolean))],
    broker: rc.broker || null, brokerPhone: rc.brokerPhone || null, brokerEmail: rc.brokerEmail || null,
    references: Array.isArray(rc.referenceNumbers) ? rc.referenceNumbers : [],
    accessorials: rc.accessorials || [],
    contacts: (rc.contacts || []).filter((c) => c && (c.phone || c.email)),
    handwritten: (rc.handwrittenNotes || []).filter(Boolean),
    bill: rc.fbfBillNumber || null, truck: rc.truckNumber || null, matchedBy: rc.matchedBy || null,
    uncertain: !!rc.raw,
  };
}

// Disagreements between the rate con and the trip sheet — both values shown,
// neither silently replaces the other.
export function documentConflicts(trip) {
  const rc = trip.rateCon; const sh = trip.manifest;
  if (!rc || !sh) return [];
  const out = [];
  const rcTemp = rc.tempSetting ? Number((String(rc.tempSetting).match(/-?\d+(?:\.\d+)?/) || [])[0]) : null;
  if (rcTemp != null && !Number.isNaN(rcTemp) && sh.maintainTempF != null && Math.abs(rcTemp - sh.maintainTempF) >= 1) {
    out.push({ kind: 'temperature', msg: `Temperature: rate con “${rc.tempSetting}” vs trip sheet ${sh.maintainTempF}°F` });
  }
  const sheetDel = (sh.stops || []).filter((s) => /DELIVER/i.test(s.action || ''));
  (rc.deliveries || []).forEach((d) => {
    const s = sheetDel.find((x) => cityKey(x.city, x.state) === cityKey(d.city, d.state));
    if (!s) { out.push({ kind: 'stop', msg: `Rate con delivery ${[d.city, d.state].filter(Boolean).join(', ')} is not on the trip sheet` }); return; }
    const rcAppt = [d.date, d.time || d.appointment].filter(Boolean).join(' ');
    const shAppt = `${s.apptDate || ''} ${s.apptTime || ''}`.trim();
    if (rcAppt && shAppt && !rcAppt.includes(s.apptTime || '\u0000')) out.push({ kind: 'appointment', msg: `${s.customer || s.city}: rate con “${rcAppt}” vs trip sheet “${shAppt}”` });
  });
  const rcOrder = (rc.deliveries || []).map((d) => cityKey(d.city, d.state));
  const shOrder = sheetDel.map((s) => cityKey(s.city, s.state)).filter((k) => rcOrder.includes(k));
  const rcInSheet = rcOrder.filter((k) => shOrder.includes(k));
  if (rcInSheet.length > 1 && rcInSheet.join('>') !== [...new Set(shOrder)].join('>')) out.push({ kind: 'sequence', msg: 'Delivery order differs between the rate con and the trip sheet' });
  return out;
}

// ---- incidents (Watchtower) ---------------------------------------------------------
// Open alerts for one trip, duplicates grouped by issue type; the same truck's
// alerts on other trips are listed as "also affects".
// The stop timer on a truck card: hidden under 15 minutes (fuel, a quick stop), amber from 1 hour on a team, red from 2. Pure.
// reported down and not closed, or customers have been told it is. Pure.
export const isDown = (trip) => !!(trip && (trip.breakdownOpen || (trip.breakdown && trip.breakdown.on)));

export function stopTimer(stopped, team, now = Date.now()) {
  if (!stopped) return null;
  const mins = Math.floor((now - stopped.since) / 60000);
  if (mins < 15) return null;
  const h = Math.floor(mins / 60);
  return { label: `Stopped ${stopped.unknown ? 'at least ' : ''}${h ? `${h}h ${mins % 60}m` : `${mins}m`}`, level: team && mins >= 120 ? 'late' : team && mins >= 60 ? 'risk' : 'calm' };
}

// The late marker on a truck card, from the open alerts only (no new ETA math): an appointment already
// missed and not delivered, a truck that will miss one, or one that may. Pure.
export function lateFlag(incidents) {
  const groups = (incidents && incidents.groups) || [];
  const passed = groups.find((g) => g.code === 'appt-passed');
  if (passed) { const m = String(passed.title).match(/— (.+?) ago/); return { level: 'late', label: m ? `Late · ${m[1]}` : 'Late' }; }
  const risk = groups.find((g) => g.code === 'late-risk');
  if (!risk) return null;
  const m = String(risk.title).match(/ by (~.+)$/);
  const by = m ? ` · ${m[1]}` : '';
  return risk.severity === 'critical' ? { level: 'late', label: `Running late${by}` } : { level: 'risk', label: `At risk${by}` };
}

export function incidentsForTrip(trip, openAlerts) {
  const mine = (openAlerts || []).filter((a) => String(a.trip) === String(trip.tripNumber));
  const groups = new Map();
  mine.forEach((a) => {
    const g = groups.get(a.code) || { code: a.code, severity: a.severity, items: [], title: a.title, detail: a.detail };
    g.items.push(a);
    if (a.severity === 'critical') g.severity = 'critical';
    groups.set(a.code, g);
  });
  const sameTruck = (openAlerts || []).filter((a) => trip.powerUnit && String(a.unit) === String(trip.powerUnit) && String(a.trip) !== String(trip.tripNumber));
  const list = [...groups.values()].sort((a, b) => (a.severity === 'critical' ? -1 : 1) - (b.severity === 'critical' ? -1 : 1));
  // owner = whoever owns the MOST SEVERE open issue (not any issue)
  const top = list[0];
  const topOwned = top ? top.items.find((a) => a.ack) : null;
  const owner = topOwned ? topOwned.ack.by : null;
  return { groups: list, count: mine.length, owner, alsoAffects: [...new Set(sameTruck.map((a) => a.trip))] };
}

// ---- LTL runs out of a hub: which warehouse it left, which night, which ARTIS route (mirrors the Truck Builder) ----
// Only loads on ARTIS's Truck Builder or with a trip sheet from the Miami / Ventura terminal — never broker loads.
// ARTIS's own warehouse + ship date first; else the trip sheet's terminal and load date ("Thursday trip sheets"), else
// the day TruckMate shows it leaving, at the hub. → { hub: 'Miami' | 'California', day: 'YYYY-MM-DD' | null } | null. Pure.
const HUBS = [['Miami', /MIAMI\s*TERMINAL|^MIAMI\b/i, 'America/New_York'], ['Ventura', /VENTURA|OXNARD/i, 'America/Los_Angeles']];
// ARTIS Truck Builder routes per hub (others found on the board are added)
export const HUB_ROUTES = { Miami: ['NE', 'MWE', 'MWW', 'CA', 'ALB-IL'], Ventura: ['NEC', 'SEC', 'MWC'] };
export function ltlRun(trip) {
  if (!trip) return null;
  const a = trip.artisRun;
  if (a && a.hub) return { hub: /ventura|calif/i.test(a.hub) ? 'Ventura' : 'Miami', day: /^\d{4}-\d{2}-\d{2}$/.test(String(a.date || '')) ? a.date : null, route: a.route ? String(a.route).toUpperCase() : null };
  if (trip.rateCon) return null;                                   // a broker load — not an LTL run
  const load = ((trip.manifest && trip.manifest.stops) || []).find((x) => /LOAD|PICK/i.test(x.action || ''));
  if (!load) return null;                                          // no trip sheet → not on ARTIS / not a run we list
  const hit = HUBS.find(([, re]) => re.test(`${load.customer || ''} ${load.city || ''}`));
  if (!hit) return null;
  const [hub, , tz] = hit;
  const d = trip.manifest && /^\d{4}-\d{2}-\d{2}$/.test(String(trip.manifest.dateLoaded || '')) ? trip.manifest.dateLoaded : null;
  if (d) return { hub, day: d, route: null };
  const left = ((trip.times && trip.times.statusHistory) || []).find((h) => /^(depship|intran|enroute)/i.test(h.status || ''));
  const at = left ? left.at : trip.times && trip.times.dispatchedAt;
  return at ? { hub, day: new Date(at).toLocaleDateString('en-CA', { timeZone: tz }), route: null } : { hub, day: null, route: null };
}
// Does a run match the search (hub 'all' | Miami | Ventura, day '' | YYYY-MM-DD, route 'all' | NE…)? Pure.
export function matchRun(r, { hub = 'all', day = '', route = 'all' } = {}) {
  return !!r && (hub === 'all' || r.hub === hub) && (!day || r.day === day) && (route === 'all' || r.route === route);
}
// "Thu 10/22" (or "Today" / "Yesterday" in Miami). Pure.
export function runDayLabel(day, now = Date.now()) {
  if (!day) return 'Day unknown';
  const today = new Date(now).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const diff = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${day}T12:00:00Z`)) / 86400000);
  const d = new Date(`${day}T12:00:00Z`);
  const short = `${d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' })} ${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  return diff === 0 ? `Today · ${short}` : diff === 1 ? `Yesterday · ${short}` : short;
}

// ---- ARTIS for a stop: its customers (client IDs for the manifest) and ARTIS's own ETA ----
const words = (x) => String(x || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !/^(INC|LLC|THE|AND|WHOLESALE|FLORAL|FLORIST|FLOWERS?)$/.test(w));
// → { customers: [{ clientId, name }], eta: ARTIS status | null }. Same town; same name first when a town has several. Pure.
export function artisForStop(s, trip) {
  const list = (trip && trip.artisStops) || [];
  if (!s || !list.length) return { customers: [], eta: null };
  const town = (x) => String(x || '').split(',')[0].toUpperCase().replace(/[^A-Z ]/g, '').trim();
  const inTown = list.filter((a) => town(a.city) && (town(a.city) === town(s.place) || town(s.place).startsWith(town(a.city)) || town(a.city).startsWith(town(s.place))));
  const named = inTown.filter((a) => words(a.name).some((w) => words(s.customer).includes(w)));
  const customers = (named.length ? named : inTown).map((a) => ({ clientId: a.clientId, name: a.name }));
  const etas = ((trip.artisEta && trip.artisEta.stops) || []).filter((e) => customers.some((c) => String(c.clientId) === String(e.clientId)));
  const eta = etas.find((e) => e.etaMs) || etas[0] || null;
  return { customers, eta: eta ? { ...eta, readAt: trip.artisEta.at || null } : null };
}

// ---- each stop's ETA + miles (the server's route ETAs — same math as alerts and Jarvis) ----
const TZ = { CT: 'America/Chicago', ET: 'America/New_York', MT: 'America/Denver', PT: 'America/Los_Angeles', AZ: 'America/Phoenix' };
const ZONE_OF = { AL: 'CT', AR: 'CT', IL: 'CT', IA: 'CT', KS: 'CT', LA: 'CT', MN: 'CT', MS: 'CT', MO: 'CT', NE: 'CT', ND: 'CT', OK: 'CT', SD: 'CT', TX: 'CT', WI: 'CT', TN: 'CT',
  CO: 'MT', ID: 'MT', MT: 'MT', NM: 'MT', UT: 'MT', WY: 'MT', AZ: 'AZ', CA: 'PT', NV: 'PT', OR: 'PT', WA: 'PT' };
// "Fri, Oct 23, 3:02 AM CT" in the stop's own time zone. Pure.
export function stopLocalTime(ms, place) {
  if (ms == null) return null;
  const z = ZONE_OF[stateOf(place)] || 'ET';
  return `${new Date(ms).toLocaleString('en-US', { timeZone: TZ[z], weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ${z === 'AZ' ? 'MT' : z}`;
}
const placeKeyOf = (label) => { const c = cityOf(label).replace(/[^A-Z ]/g, '').trim(); const st = stateOf(label).replace(/[^A-Z]/g, ''); return c && st ? `${c}|${st}` : null; };
const zipIn = (label) => (String(label || '').match(/\b(\d{5})\b/) || [])[1] || null;
// One stop's ETA from the server's route: matched by ZIP or town (the stop's own place, or its bills' TruckMate
// town when the sheet names it differently). → { etaMs, miles, legMiles, passed } | null. Pure.
export function stopEta(s, trip, srv) {
  if (!srv || !s || s.action !== 'Delivery') return null;
  const places = [s.place, ...(trip && trip.bills ? trip.bills.filter((b) => (s.bills || []).includes(b.billNumber)).map((b) => b.to) : [])].filter(Boolean);
  const zips = new Set(places.map(zipIn).filter(Boolean)); const keys = new Set(places.map(placeKeyOf).filter(Boolean));
  const hit = (x) => (x.zip && zips.has(String(x.zip))) || keys.has(placeKeyOf(x.label));
  if ((srv.passed || []).some(hit)) return { passed: true };
  const list = srv.stops || [];
  const i = list.findIndex(hit);
  if (i < 0) return null;
  const leg = list[i];
  return { etaMs: leg.etaMs, miles: leg.miles, legMiles: i > 0 && leg.miles != null && list[i - 1].miles != null ? Math.max(0, leg.miles - list[i - 1].miles) : null, apptMs: leg.apptMs || null, apptFrom: leg.apptFrom || null };
}

// ---- appointment / ETA at a glance ------------------------------------------------
const CRUISE_MPH = 60;   // dispatch rule: 60 mph for the whole run (same as the server)
export function estimateArrival(miles, { team, driveLeftMin, shiftLeftMin, now = Date.now() } = {}) {
  if (!miles || miles <= 0) return null;
  const H = 3600000; let driveHrs = miles / CRUISE_MPH; let t = now;
  if (team) return t + driveHrs * (24 / 20) * H;
  let first = driveLeftMin != null ? driveLeftMin / 60 : 11;
  if (shiftLeftMin != null) first = Math.min(first, shiftLeftMin / 60);
  first = Math.max(first, 0);
  let d = Math.min(driveHrs, first); t += (d + (d > 8 ? 0.5 : 0)) * H; driveHrs -= d;
  let guard = 0;
  while (driveHrs > 0.01 && guard++ < 60) { t += 10 * H; d = Math.min(driveHrs, 11); t += (d + (d > 8 ? 0.5 : 0)) * H; driveHrs -= d; }
  return t;
}
// A broker load: a rate con is on it, or its bills are B numbers (broker bills) and it isn't on an ARTIS run.
// Broker loads get a rate con, not ARTIS trip manifests. Pure.
export function isBrokerLoad(trip) {
  if (!trip) return false;
  if (trip.rateCon) return true;
  if (trip.artisRun) return false;
  const bills = (trip.bills || []).map((b) => String(b.billNumber || '').trim()).filter(Boolean);
  return bills.length > 0 && bills.length <= 12 && bills.every((b) => /^B\d/i.test(b));
}
export function handlingModel(trip) {
  const list = (trip.rateCon && Array.isArray(trip.rateCon.specialInstructions)) ? [...new Set(trip.rateCon.specialInstructions.map((x) => String(x).trim()).filter(Boolean))] : [];
  const checks = trip.rcCheck || {};
  const done = list.filter((s) => checks[s] && checks[s].done).length;
  if (!list.length) return { label: !trip.rateCon && isBrokerLoad(trip) ? 'Rate con missing' : 'No instructions on file', pending: 0, total: 0 };
  return { label: done === list.length ? `All ${list.length} signed off` : `${list.length - done} of ${list.length} pending`, pending: list.length - done, total: list.length };
}

// ---- outside carriers (OC) -------------------------------------------------------
export const TRACKING_LABEL = {
  check_call: 'Check calls', driver_link: 'Driver tracking link', eld_share: 'Carrier ELD sharing', platform: 'Tracking platform', email: 'Carrier emails',
};
export const CHECKIN_OVERDUE_H = 4;
// Everything the OC card shows. Our ELD / engine data never applies to an
// outside carrier's truck, so those read "not shared", never "unknown/healthy".
export function ocModel(trip, now = Date.now()) {
  const oc = trip.oc;
  if (!oc) return null;
  const list = trip.checkins || [];
  const last = list[0] || null;
  const ckMs = last ? Date.parse(last.at) : null;
  // a location from the driver's tracking link counts as an update too
  const pingMs = trip.driverLink && trip.driverLink.lastPingAt ? Date.parse(trip.driverLink.lastPingAt) : null;
  const byPing = pingMs != null && (ckMs == null || pingMs > ckMs);
  const lastMs = byPing ? pingMs : ckMs;
  const hrs = lastMs ? (now - lastMs) / 3600000 : null;
  const c = oc.carrier || {};
  return {
    carrierName: c.name || 'Carrier not identified',
    carrierId: c.id || null,
    unsaved: !!c.unsaved || !c.id,
    code: oc.code || null,
    truck: oc.truck || null,
    trailer: oc.trailer || trip.trailer || null,
    driverName: oc.driverName || null,
    driverPhone: oc.driverPhone || null,
    driver2Name: oc.driver2Name || null,
    driver2Phone: oc.driver2Phone || null,
    crew: oc.crew === 'team' ? 'team' : 'solo',
    // required: every driver's name + phone (solo 1 / team 2), truck #, trailer #
    missing: oc.missing || [],
    infoFrom: oc.infoFrom || null,
    smsConsent: oc.smsConsent || null,   // driver agreed to load texts (who recorded it, when)
    dispatchPhone: c.dispatchPhone || null,
    email: c.email || null,
    trackingEmail: c.trackingEmail || null,
    mc: c.mc || null,
    dot: c.dot || null,
    method: TRACKING_LABEL[c.trackingMethod || 'check_call'] || 'Check calls',
    source: oc.source,
    evidence: oc.evidence || null,
    checkins: list,
    last,
    lastAgo: lastMs ? ago(lastMs, now) : null,
    overdue: hrs == null || hrs >= CHECKIN_OVERDUE_H,
    lastLabel: byPing ? `Driver app location · ${ago(lastMs, now)}`
      : last ? `${last.source === 'email' ? 'Email' : last.source === 'check call' ? 'Check call' : last.source === 'driver app' ? 'Driver check-in' : 'Update'} · ${ago(lastMs, now)}` : 'No check-in yet',
    link: driverLinkModel(trip, now),
  };
}

// ---- OC driver tracking link (TagAlong app) ------------------------------------------
const LINK_STATUS = {
  created: ['Link created — not sent yet', 'neutral'],
  sent: ['Sent — driver has not opened it yet', 'neutral'],
  opened: ['Opened — location not shared yet', 'warning'],
  sharing: ['Sharing live location', 'ok'],
  quiet: ['Went quiet — no location lately', 'warning'],
  stopped: ['Driver stopped sharing', 'warning'],
  completed: ['Ended at delivery', 'neutral'],
  revoked: ['Turned off', 'neutral'],
  expired: ['Expired', 'neutral'],
};
export function driverLinkModel(trip, now = Date.now()) {
  const l = trip.driverLink;
  if (!l) return null;
  const [label, tone] = LINK_STATUS[l.status] || ['Unknown', 'neutral'];
  const p = l.lastPing || null;
  return {
    ...l,
    label, tone,
    live: !['completed', 'revoked', 'expired'].includes(l.status),
    sentLabel: l.sentAt ? `${l.sentVia || 'Sent'} · ${ago(l.sentAt, now)}${l.sentTo ? ` · ${l.sentTo}` : ''}` : 'Not sent yet',
    openedLabel: l.openedAt ? `${l.openedVia === 'app' ? 'TagAlong app' : 'Browser'} · ${ago(l.openedAt, now)}` : 'Not opened yet',
    lastFix: p ? `${ago(p.at, now)}${p.speedMph != null ? ` · ${p.speedMph} mph` : ''}${p.accuracyM != null ? ` · ±${p.accuracyM} m` : ''}` : null,
    viaApp: l.openedVia === 'app',
  };
}

// ---- trip packet pages --------------------------------------------------------------
export const PAGE_TYPE_LABEL = { driver_instructions: 'Driver instructions', loading_sheet: 'Loading sheet',
  manifest: 'Manifest', manifest_continuation: 'Manifest (cont.)', rate_confirmation: 'Rate confirmation', carrier_confirmation: 'Carrier confirmation',
  email: 'Email', bill_of_lading: 'Bill of lading', packing_slip: 'Packing slip', shipping_ticket: 'Shipping ticket', proof_of_delivery: 'Proof of delivery',
  driver_id: 'Driver ID (restricted)', invoice: 'Invoice', shipment_notice: 'Shipment notice', other: 'Other',
};
const PAGE_ORDER = ['manifest', 'manifest_continuation', 'rate_confirmation', 'carrier_confirmation', 'email', 'bill_of_lading', 'packing_slip', 'shipping_ticket', 'proof_of_delivery', 'invoice', 'shipment_notice', 'other', 'driver_id'];
// The trip's manifest — THE trip sheet of the load: its own page(s) (one, or two
// with the continuation page), opened together as one document.
export function tripSheetModel(trip) {
  const sh = trip.manifest || null;
  let ids = sh && Array.isArray(sh.docIds) ? sh.docIds.map(String) : [];
  if (!ids.length) {
    ids = (trip.packet || []).filter((p) => /^manifest/.test(p.type || '') && p.docId)
      .sort((a, b) => (a.file - b.file) || (a.page - b.page)).map((p) => String(p.docId));
  }
  ids = [...new Set(ids)];
  if (!ids.length) return null;
  return {
    ids, pages: ids.length,
    name: `Trip sheet ${trip.tripNumber}`,
    uploadedAt: sh ? sh.uploadedAt : null, uploadedBy: sh ? sh.uploadedBy : null, version: sh ? sh.version || 1 : null,
  };
}

export function packetModel(trip) {
  // the manifest pages are shown as the trip sheet itself, not as packet pages
  const pages = (trip.packet || []).filter((p) => !/^manifest/.test(p.type || ''));
  const groups = PAGE_ORDER.map((type) => ({
    type, label: PAGE_TYPE_LABEL[type],
    pages: pages.filter((p) => (p.type || 'other') === type).sort((a, b) => (a.file - b.file) || (a.page - b.page)),
  })).filter((g) => g.pages.length);
  return { groups, count: pages.length, restricted: pages.filter((p) => p.type === 'driver_id').length };
}

// ---- fleet map ------------------------------------------------------------------
export const FLEET_STATE = {
  moving: { label: 'Moving', color: '#16a34a' },
  idling: { label: 'Idling', color: '#d97706' },
  parked: { label: 'Parked', color: '#475569' },
  stale: { label: 'No recent GPS', color: '#9ca3af' },
};
// How long something has sat still, as "3d 4h" / "5h 10m" (null when moving / unknown).
export function stillMs(x, now = Date.now()) {
  if (!x || !x.stillSince) return null;
  const ms = now - Date.parse(x.stillSince);
  return Number.isNaN(ms) || ms < 0 ? null : ms;
}
export function durLabel(ms) {
  if (ms == null) return null;
  const m = Math.floor(ms / 60000); const h = Math.floor(m / 60); const d = Math.floor(h / 24);
  return d ? `${d}d ${h % 24}h` : h ? `${h}h ${m % 60}m` : `${m}m`;
}
// What the fleet map shows for the current filters. Trailers show on their own
// whenever "Trailers" is on (a hitched one sits beside its truck).
// f.assigned / f.unassigned / f.idleOnly filter trailers; f.idleDays is the
// "hasn't moved for X days" alert line.
// a truck whose Samsara / GPS hasn't reported in 10+ days and isn't parked at our Miami or Ventura hub. Pure.
export const OFFLINE_DAYS = 10;
export function isOffline(t, now = Date.now()) {
  if (!t || t.kind === 'trailer' || t.atHub) return false;
  const at = Date.parse(t.gpsAt || '');
  return Number.isFinite(at) && now - at >= OFFLINE_DAYS * 86400000;   // no time at all → can't tell, keep it on the map
}
export function fleetView(data, f = {}, now = Date.now()) {
  const q = String(f.query || '').trim().toLowerCase();
  const states = f.states || ['moving', 'idling', 'parked', 'stale'];
  const idleMs = (f.idleDays != null ? Number(f.idleDays) : 3) * 86400000;
  const isIdle = (t) => { const ms = stillMs(t, now); return ms != null && ms >= idleMs; };
  const match = (x) => !q || [x.unit, x.trailer, x.driver, x.trip, x.destination, x.location, x.truck, x.oc, x.lastTrip, x.lastTruck].filter(Boolean).join(' ').toLowerCase().includes(q);
  // not reporting 10+ days and not at one of our hubs (Miami / Ventura) → kept off the map, in their own list
  const offline = (t) => isOffline(t, now);
  const trucks = ((data && data.trucks) || []).filter((t) => (f.offlineOnly ? offline(t) : f.trucks !== false && !offline(t) && states.includes(t.state) && (!f.onTrip || t.trip)) && match(t));
  const trailers = ((data && data.trailers) || []).filter((t) => f.trailers !== false
    && (t.assigned !== false ? f.assigned !== false : f.unassigned !== false)
    && (!f.idleOnly || isIdle(t)) && (!f.onTrip || t.trip) && match(t)).map((t) => ({ ...t, idle: isIdle(t) }));
  const everyTruck = (data && data.trucks) || [];
  const all = everyTruck.filter((t) => !offline(t));
  const allTr = (data && data.trailers) || [];
  const count = (st) => all.filter((t) => t.state === st).length;
  return {
    trucks, trailers,
    counts: {
      trucks: all.length, offline: everyTruck.length - all.length, atHub: all.filter((t) => t.atHub).length, moving: count('moving'), idling: count('idling'), parked: count('parked'), stale: count('stale'), onTrip: all.filter((t) => t.trip).length,
      trailers: allTr.length, assigned: allTr.filter((t) => t.assigned !== false).length, unassigned: allTr.filter((t) => t.assigned === false).length, idle: allTr.filter(isIdle).length,
    },
  };
}

// ---- texting opt-in (must match the registered 10DLC campaign word for word) ----
export const SMS_PRIVACY_URL = 'https://www.floridabeauty.com/site_use.html';
export const OPTIN_SCRIPT = `Florida Beauty Flora Dispatch would like to text you about your assigned loads: stop confirmations, requests for your POD and BOL, and dispatch updates. Message frequency varies by load. Message and data rates may apply. Reply HELP for help or STOP to opt out at any time. Agreeing is not required to haul for us. You can read our privacy policy at ${SMS_PRIVACY_URL}. Do you agree to receive these texts?`;

// every stop's customer, town and references on the trip sheet — a customer billed under another name
// (Accardi Food on trip 624891 is billed as THE RICOTTA & CHEESE FACTORY) is still found by its own name. Pure.
export const stopWords = (t) => ((t && t.manifest && t.manifest.stops) || []).map((x) => `${x.customer || ''} ${x.city || ''} ${(x.references || []).join(' ')}`).join(' ');

// ---- Fleet map: customers (ARTIS geofences) and the loads headed to them ----
const escH = (x) => String(x == null ? '' : x).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
// "Mon, Oct 26 · 6:55 AM ET" (Eastern, like every other ETA in the console). Pure.
export const etaLabel = (ms) => {
  if (!ms) return null;
  const d = new Date(ms);
  const day = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time} ET`;
};
// the loads headed to a customer, earliest ETA first; loads without an ETA last. Pure.
export const incomingSorted = (coming = []) => [...coming].sort((a, b) => (a.etaMs || Infinity) - (b.etaMs || Infinity));
export const fenceWhere = (f) => [f.address, [f.city, f.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
// customer search: name, city, state, zip, address, ARTIS id. Pure.
export function fenceMatches(f, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${f.name} ${f.city || ''} ${f.state || ''} ${f.zip || ''} ${f.address || ''} ${f.id}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}
// The hover preview for a customer: name, address, incoming count, the earliest ETA (most prominent), and every
// load's trip / truck / trailer / ETA. Never makes up an ETA. Pure (HTML string, everything escaped).
// the drivers on a load and their hours, for the hover ("Jaime Rivera · Sleeper berth · Drive 11h · Shift 14h"). Pure.
function tipDrivers(c) {
  if (!c || !c.truck || c.oc) return '';
  const names = c.drivers || [];
  if (!names.length) return '<span class="fm-tip-added">No driver assigned yet</span>';
  return names.map((n, i) => { const h = (c.driverHours || [])[i]; return `<span class="fm-tip-driver"><b>${escH(n)}</b>${h ? ` · ${escH(dutyLabel(h.status))} · Drive ${escH(fmtMins(h.driveLeftMin))} · Shift ${escH(fmtMins(h.shiftLeftMin))}` : ''}</span>`; }).join('');
}
export function fenceTip(f, coming = []) {
  const loads = incomingSorted(coming);
  const where = fenceWhere(f);
  const head = `<div class="fm-tip-name">${escH(f.name)}</div>${where ? `<div class="fm-tip-where">${escH(where)}</div>` : ''}`;
  if (!loads.length) return `<div class="fm-tip">${head}<div class="fm-tip-none">No incoming loads</div></div>`;
  const first = loads[0];
  const firstEta = etaLabel(first.etaMs);
  const rows = loads.map((c) => `<div class="fm-tip-load"><span>Trip <b>${escH(c.trip)}</b></span><span>Truck <b>${escH(c.truck || '—')}</b></span><span>Trailer <b>${escH(c.trailer || '—')}</b></span><span class="fm-tip-eta">${escH(etaLabel(c.etaMs) ? `ETA ${etaLabel(c.etaMs)}${c.milesAway != null ? ` · ${Number(c.milesAway).toLocaleString()} mi away` : ''}` : noEtaReason(c))}</span>${addedOrOutbound(c) ? `<span class="fm-tip-added">${escH(addedOrOutbound(c).label)} ${escH(addedOrOutbound(c).value)}</span>` : ''}${tipDrivers(c)}</div>`).join('');
  return `<div class="fm-tip">${head}<div class="fm-tip-count">${loads.length} incoming load${loads.length === 1 ? '' : 's'}</div>`
    + `<div class="fm-tip-label">Estimated arrival</div><div class="fm-tip-big">${escH(firstEta || noEtaReason(first))}</div>${rows}</div>`;
}
// Group map points that would overlap at this zoom: one cluster per grid cell (cell px wide) — kept per kind, so
// customers / trucks / trailers never merge. points: [{ id, x, y }] in screen pixels. Pure.
export function gridClusters(points = [], cell = 56) {
  const cells = new Map();
  for (const p of points) {
    const k = `${Math.floor(p.x / cell)}:${Math.floor(p.y / cell)}`;
    if (!cells.has(k)) cells.set(k, []);
    cells.get(k).push(p);
  }
  return [...cells.values()].map((members) => ({ members, x: members.reduce((s, m) => s + m.x, 0) / members.length, y: members.reduce((s, m) => s + m.y, 0) / members.length }));
}

// ---- trip history (Fleet map) -------------------------------------------------
// what each kind of stop looks like on the map and in the timeline
export const STOP_KIND = {
  rest: { color: '#7C3AED', label: 'Rest / reset' },
  delivery: { color: '#16A34A', label: 'Delivery' },
  pickup: { color: '#0D9488', label: 'Pickup' },
  fuel: { color: '#0891B2', label: 'Fuel stop' },
  work: { color: '#D97706', label: 'On duty / work' },
  swap: { color: '#2563EB', label: 'Driver change' },
  breakdown: { color: '#DC2626', label: 'Breakdown' },
  pc: { color: '#DB2777', label: 'Personal conveyance' },
  stop: { color: '#64748B', label: 'Stopped' },
};
// One timeline from the journey: stops and events (status changes, driver changes, texts, calls,
// road calls), oldest first, each with a stable key. `show`: all | stops | messages. Pure.
export function journeyTimeline(j, show = 'all') {
  if (!j) return [];
  const items = [
    ...(j.stops || []).map((s) => ({ key: `s${s.n}`, kind: 'stop', at: s.from, stop: s, lat: s.lat, lng: s.lng })),
    ...(j.events || []).map((e, i) => ({ key: `e${i}`, kind: e.type, at: e.at, ev: e, lat: e.lat, lng: e.lng })),
  ].sort((a, b) => String(a.at).localeCompare(String(b.at)));
  if (show === 'stops') return items.filter((x) => x.kind === 'stop' || x.kind === 'roadcall');
  if (show === 'messages') return items.filter((x) => ['text', 'reply', 'call'].includes(x.kind));
  return items;
}

// TruckMate's own date/time as written ("2026-10-28T07:00:00" → "Oct 28, 7:00 AM"; midnight → just the day). Pure.
export function tmDate(v) {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return null;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1];
  const day = `${mon} ${Number(m[3])}`;
  const h = Number(m[4] || 0); const mi = m[5] || '00';
  if (!h && mi === '00') return day;
  return `${day}, ${h % 12 || 12}:${mi} ${h < 12 ? 'AM' : 'PM'}`;
}
// an incoming load not picked up yet (TruckMate: assigned / dispatched / planned) — it has no ETA until it is. Pure.
export const notPickedUp = (c) => /^(ASSGN|ASSIGNED|DISP|AVAIL|AVBL|PLAN|NEW|PRINTED|LOADINGMAN|LOADEDTOGO)/i.test(String((c && (c.statusCode || c.status)) || ''));

// Why an incoming load has no ETA — the headline on the customer card / hover. Pure.
export function noEtaReason(c) {
  if (!c) return 'Not available yet';
  if (!c.truck) return 'No truck assigned yet';
  // the truck is still at our Miami / Ventura hub and hasn't left — no ETA until it rolls
  if (c.atHub && !c.departed) return `Still at the ${c.atHub} hub — not departed yet`;
  const code = String(c.statusCode || c.status || '').toUpperCase();
  const when = c.statusAt ? ` · ${new Date(c.statusAt).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET` : '';
  // TruckMate's latest status is the news when we can't track the truck (outside carriers, no GPS)
  if (/^(DELVD|DELIV|DEL$|DELIVERED|CMPLT|COMPLETE)/.test(code)) return `Delivered${when} (TruckMate)`;
  if (/^(ARRCONS|ARRIVE CONSIGNEE)/.test(code)) return c.stopsLeft === 1 ? `Arrived${when} (TruckMate)` : `At another stop on the route${when} (TruckMate)`;
  if (/^(DEPCONS|DEPART CONSIGNEE)/.test(code)) return `Left a stop${when} — on the way (TruckMate)`;
  if (/^(ARRSHIP|ARRIVE SHIPPER)/.test(code)) return `At the shipper${when} (TruckMate)`;
  if (/^(DEPSHIP|DEPART SHIPPER|INTRAN|IN TRANSIT|ENROUTE)/.test(code)) return `On the way · left the shipper${when} (TruckMate)`;
  if (notPickedUp(c)) return 'Not picked up yet';
  if (c.oc && !c.truckNow) return 'Outside carrier — no tracking';
  if (!c.truckNow) return 'No GPS from the truck';
  return 'Not available yet';
}
// a TruckMate due date (as written) that's already behind us. Pure.
export function pastDue(v, now = Date.now()) {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const today = new Date(now).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  return `${m[1]}-${m[2]}-${m[3]}` < today;
}

// documents Jarvis pulled from Synergize are labeled "Synergize" (older records said "ARTIS (Synergize)"). Pure.
export const byLabel = (v) => (v == null ? v : String(v).replace(/ARTIS\s*\(Synergize\)|ARTIS Synergize/g, 'Synergize').replace(/^Jarvis \(ARTIS\)$/, 'Jarvis (Synergize)'));

// "Added Oct 24, 10:15 AM" until the truck leaves, then "Outbound Mon, Oct 26, 7:40 PM ET". Pure; null when unknown.
export function addedOrOutbound(c) {
  if (!c) return null;
  // once the truck has left: its OUTBOUND day — the day the trip was built (Monday's boxes rolling out Tuesday 1 AM are
  // Monday's outbound), never the time it departed
  if (c.leftAt || c.pickedUpAt) {
    const m = String(c.outboundDay || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return { label: 'Outbound', value: new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' }) };
  }
  if (c.addedAt && tmDate(c.addedAt)) return { label: 'Added', value: tmDate(c.addedAt) };
  return null;
}

// ---- not departed yet, truck still at our hub (no ETA while it sits) ----
const HUB_POINTS = [
  { hub: 'Miami', lat: 25.7947, lng: -80.3099, mi: 1 }, { hub: 'Miami', lat: 25.8026, lng: -80.3102, mi: 1 }, { hub: 'Miami', lat: 25.8062, lng: -80.3180, mi: 1 },
  { hub: 'Ventura', lat: 34.2746, lng: -119.229, mi: 1.5 },
];   // same as the server (Miami yard lots + cooler, Ventura hub)
const miBetween = (a, b, c, d) => { const R = 3958.8; const r = Math.PI / 180; const x = Math.sin((c - a) * r / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin((d - b) * r / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(x)); };
export function hubOf(lat, lng) {
  if (lat == null || lng == null) return null;
  const h = HUB_POINTS.find((p) => miBetween(lat, lng, p.lat, p.lng) <= p.mi);
  return h ? h.hub : null;
}
// TruckMate hasn't marked it departed (no depart / in-transit status, no bill picked up) and the truck's GPS is at
// our Miami or Ventura hub → 'Miami' / 'Ventura'; else null. Pure.
export function heldAtHub(trip) {
  if (!trip) return null;
  const hist = ((trip.times && trip.times.statusHistory) || []).map((x) => String(x.status || ''));
  const rolled = /^(DEPSHIP|INTRAN|ENROUTE|ARRCONS|DEPCONS|DELV|CMPLT|COMPLETE)/i;
  if (rolled.test(String(trip.statusCode || '')) || hist.some((x) => rolled.test(x))) return null;
  if ((trip.bills || []).some((b) => b && b.actualPickup)) return null;
  const l = trip.live || {};
  return hubOf(l.lat, l.lng);
}
